import React, { useState, useEffect, useRef } from 'react';
import { FaComments, FaTimes, FaWhatsapp, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { toast } from 'react-toastify';

const ChatBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [showQuestions, setShowQuestions] = useState(false);
  const messagesEndRef = useRef(null);

  const staticQuestions = [
    {
      question: "What are your delivery options?",
      answer:
        "We offer reliable delivery options for domestic and international orders. Shipping methods and timelines depend on the order quantity, product type, and delivery location. For bulk or export orders, our team will provide complete shipping details before dispatch.",
    },
    {
      question: "How can I track my order?",
      answer:
        "Once your order is dispatched, we will share the shipment tracking details through email or WhatsApp. For any order updates, you can contact us at +91 75670 66690 or email us at contact@nutrajun.com.",
    },
    {
      question: "What is your return policy?",
      answer:
        "Due to the nature of food products, we do not accept returns once an order has been confirmed and dispatched. We ensure all products are carefully checked and securely packed before shipping. For any order-related concerns, please contact our support team.",
    },
    {
      question: "Are your products organic?",
      answer:
        "Nutrajun LLP offers premium quality freeze-dried fruits, vegetables, herbs, and food ingredients made from carefully selected raw materials. Product specifications and certification details can be provided based on customer requirements.",
    },
    {
      question: "Do you ship internationally?",
      answer:
        "Yes, we supply our freeze-dried products worldwide. We support international buyers, importers, distributors, wholesalers, food manufacturers, and private label brands with bulk supply solutions.",
    },
    {
      question: "What payment methods do you accept?",
      answer:
        "We accept secure payment options for domestic and international orders. Payment details and available methods will be shared during order confirmation.",
    },
    {
      question: "How should I store freeze-dried products?",
      answer:
        "Our freeze-dried products should be stored in a cool, dry place away from direct sunlight and moisture. After opening, keep the product properly sealed to maintain freshness, crunchiness, and quality.",
    },
    {
      question: "Do you provide product samples?",
      answer:
        "Yes, product samples are available for selected freeze-dried products. You can contact our team for sample availability, charges, and delivery details via WhatsApp at +91 75670 66690 or email us at contact@nutrajun.com.",
    },
    {
      question: "What is the shelf life of freeze-dried products?",
      answer:
        "Our freeze-dried products have a shelf life of up to 24 months when stored properly in recommended storage conditions. Advanced freeze-drying technology helps preserve the product's natural taste, color, and quality for a longer period.",
    },
  ];

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleOpenChat = () => {
    setIsOpen(true);
    setMessages([
      {
        id: 1,
        type: 'bot',
        content: 'Hello! 👋 Welcome to Nutrajun. How can I help you today?',
        timestamp: new Date()
      }
    ]);
  };

  const handleCloseChat = () => {
    setIsOpen(false);
    setMessages([]);
    setShowQuestions(false);
  };

  const handleQuestionClick = (question) => {
    const botMessage = {
      id: messages.length + 1,
      type: 'bot',
      content: question.answer,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, {
      id: messages.length + 1,
      type: 'user',
      content: question.question,
      timestamp: new Date()
    }, botMessage]);

    setShowQuestions(false);
  };

  const handleWhatsAppContact = () => {
    const phoneNumber = '7567066690';
    const message = 'Hello! I need help with my Nutrajun order.';
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');

    const userMessage = {
      id: messages.length + 1,
      type: 'user',
      content: 'I want to contact via WhatsApp',
      timestamp: new Date()
    };

    const botMessage = {
      id: messages.length + 2,
      type: 'bot',
      content: 'Great! I\'ve opened WhatsApp for you. You can also reach us directly at +91 98765 43210',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage, botMessage]);
    setShowQuestions(false);
  };

  return (
    <>
      {/* Chat Bot Toggle Button */}
      <div className="fixed bottom-6 right-6 z-50">
        <button
          onClick={isOpen ? handleCloseChat : handleOpenChat}
          className="bg-green-500 hover:bg-green-600 text-white rounded-full p-4 shadow-lg transition-all duration-300 transform hover:scale-110"
        >
          {isOpen ? <FaTimes size={24} /> : <FaComments size={24} />}
        </button>
      </div>

      {/* Chat Bot Modal */}
      {isOpen && (
        <div className="fixed bottom-24 right-6 z-50 w-[25rem] h-[484px] bg-white rounded-lg shadow-2xl border border-gray-200 flex flex-col">
          {/* Header */}
          <div className="bg-green-500 text-white p-4 rounded-t-lg flex justify-between items-center">
            <div>
              <h3 className="font-semibold">Nutrajun Support</h3>
              <p className="text-sm opacity-90">We're here to help!</p>
            </div>
            <button
              onClick={handleCloseChat}
              className="text-white hover:text-gray-200"
            >
              <FaTimes size={16} />
            </button>
          </div>

          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-xs px-3 py-2 rounded-lg ${message.type === 'user'
                    ? 'bg-green-500 text-white'
                    : 'bg-gray-100 text-gray-800'
                    }`}
                >
                  <p className="text-sm">{message.content}</p>
                  <p className="text-xs opacity-70 mt-1">
                    {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Actions */}
          <div className="p-4 border-t border-gray-200">
            {/* Questions Toggle */}
            <button
              onClick={() => setShowQuestions(!showQuestions)}
              className="w-full mb-3 bg-gray-100 hover:bg-gray-100 text-gray-700 py-2 px-4 rounded-lg flex items-center justify-between transition-colors"
            >
              <span className="text-sm font-medium">Frequently Asked Questions</span>
              {showQuestions ? <FaChevronUp size={16} /> : <FaChevronDown size={16} />}
            </button>

            {/* Questions List */}
            {showQuestions && (
              <div className="mb-3 max-h-32 overflow-y-auto space-y-2">
                {staticQuestions.map((q) => (
                  <button
                    key={q.id}
                    onClick={() => handleQuestionClick(q)}
                    className="w-full text-left text-xs bg-blue-50 hover:bg-blue-100 text-blue-700 py-2 px-3 rounded transition-colors"
                  >
                    {q.question}
                  </button>
                ))}
              </div>
            )}

            {/* WhatsApp Contact Button */}
            <button
              onClick={handleWhatsAppContact}
              className="w-full bg-green-500 hover:bg-green-600 text-white py-2 px-4 rounded-lg flex items-center justify-center space-x-2 transition-colors"
            >
              <FaWhatsapp size={16} />
              <span className="text-sm font-medium">Contact via WhatsApp</span>
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatBot; 